import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navigation from './Navigation';
import Footer from './Footer';
import './App.css';

// Helper function to format currency in Indian numbering system
const formatIndianCurrency = (amount) => {
  // Convert to number if it's a string
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  // Return '0' if invalid or zero
  if (!numAmount || isNaN(numAmount)) return '0';
  
  // Format using Indian locale
  return numAmount.toLocaleString('en-IN');
};

// Car Card Component with dynamic height calculation
const CarCard = React.memo(({ car, category }) => {
  const containerRef = useRef(null);
  const [imageHeight] = useState(400); // Fixed height to prevent flicker

  // Memoize image URL with fixed height
  const imageUrl = useMemo(() => {
    return car.image 
      ? `https://vidaai.com/cdn-cgi/imagedelivery/Hr3zVE-rhnx6pqKYXYRDzw/${car.image}/h=${imageHeight}` 
      : "/logo.png";
  }, [car.image, imageHeight]);

  return (
    <Link 
      to={`/listing/${category}/${car.link}`}
      className="car-card-link"
    >
      <div className="car-card">
        <div className="car-image-container" ref={containerRef}>
          <img 
            src={imageUrl}
            alt={`${car.make} ${car.model}`} 
            className="car-card-image"
            loading="lazy"
          />
        </div>
        <div className="car-info-container">
          <h3 className="car-title">{car.make} {car.model}</h3>
          <div className="car-details">
            {(car.displayPrice || car['4-hr-old'] || car['4-hr']) && (
              <div className="detail-item price">
                <span className="price-label">Starting</span>
                <span className="price-amount">₹{formatIndianCurrency(car.displayPrice || car['4-hr-old'] || car['4-hr'])}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
});

function Listing() {
  const { category } = useParams();
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMake, setSelectedMake] = useState('all');
  const [sortOrder, setSortOrder] = useState('default');
  const hasFetched = useRef(false);
  const currentCategory = useRef(category);

  // Helper function to format category name for display
  const formatCategoryName = (categorySlug) => {
    return categorySlug
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Get unique makes from cars array
  const uniqueMakes = useMemo(() => {
    const makes = [...new Set(cars.map(car => car.make))].filter(Boolean);
    return makes.sort();
  }, [cars]);

  // Filter cars based on selected make
  const filteredCars = useMemo(() => {
    if (selectedMake === 'all') {
      return cars;
    }
    return cars.filter(car => car.make === selectedMake);
  }, [cars, selectedMake]);

  // Sort filtered cars based on sort order
  const sortedAndFilteredCars = useMemo(() => {
    const carsCopy = [...filteredCars];
    
    if (sortOrder === 'low-to-high') {
      return carsCopy.sort((a, b) => (a.displayPrice || 0) - (b.displayPrice || 0));
    } else if (sortOrder === 'high-to-low') {
      return carsCopy.sort((a, b) => (b.displayPrice || 0) - (a.displayPrice || 0));
    }
    
    return carsCopy; // default order
  }, [filteredCars, sortOrder]);

  // Reusable header component
  const Header = ({ children }) => (
    <header className="App-header">
      {children}
    </header>
  );

  useEffect(() => {
    // Reset fetch flag if category changes
    if (currentCategory.current !== category) {
      hasFetched.current = false;
      currentCategory.current = category;
    }

    // Prevent duplicate fetch in strict mode
    if (hasFetched.current) return;
    hasFetched.current = true;

    const fetchCars = async () => {
      try {
        setLoading(true);
        const response = await fetch(`https://api.vidaai.com/api/v1/cars/${category}`);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        
        // Check if the API response has the expected format
        if (data.success && Array.isArray(data.cars)) {
          // Process cars to calculate displayPrice
          const processedCars = data.cars.map(car => ({
            ...car,
            // Use 4-hr-old as primary price, fall back to 4-hr if not available or zero
            displayPrice: (car['4-hr-old'] && car['4-hr-old'] > 0) ? car['4-hr-old'] : car['4-hr']
          }));
          setCars(processedCars);
        } else if (data.success === false) {
          throw new Error('API returned success: false');
        } else {
          throw new Error('Invalid API response format');
        }
      } catch (err) {
        setError(err.message);
        console.error('Error fetching cars:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCars();
  }, [category]);

  if (loading) {
    return (
      <div className="App">
        <Navigation />
        <div className="top-breadcrumb-container">
          <nav className="breadcrumb-nav">
            <Link to="/" className="breadcrumb-item">
              <span>Home</span>
            </Link>
            <span className="breadcrumb-separator">›</span>
            <span className="breadcrumb-item current">
              {formatCategoryName(category)}
            </span>
          </nav>
        </div>
        <Header>
          <h1 className="listing-page-title">{formatCategoryName(category)}</h1>
          <div className="loading">Loading cars...</div>
        </Header>
        
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="App">
        <Navigation />
        <div className="top-breadcrumb-container">
          <nav className="breadcrumb-nav">
            <Link to="/" className="breadcrumb-item">
              <span>Home</span>
            </Link>
            <span className="breadcrumb-separator">›</span>
            <span className="breadcrumb-item current">
              {formatCategoryName(category)}
            </span>
          </nav>
        </div>
        <Header>
          <h1 className="listing-page-title">{formatCategoryName(category)}</h1>
          <div className="error">
            <p>Error loading cars: {error}</p>
            <p>Make sure the API server is running at https://api.vidaai.com</p>
          </div>
        </Header>
        
        <Footer />
      </div>
    );
  }

  return (
    <div className="App">
      <Navigation />
      <div className="top-breadcrumb-container">
        <nav className="breadcrumb-nav">
          <Link to="/" className="breadcrumb-item">
            <span>Home</span>
          </Link>
          <span className="breadcrumb-separator">›</span>
          <span className="breadcrumb-item current">
            {formatCategoryName(category)}
          </span>
        </nav>
      </div>
      <Header>
        <h1 className="listing-page-title">{formatCategoryName(category)}</h1>
        <p className="listing-description">Browse our comprehensive listing of premium wedding cars in the "{formatCategoryName(category)}" category. Each vehicle in our fleet is carefully selected and maintained to ensure your special day is nothing short of perfect.</p>
        
        {/* Filter and Sort Section */}
        {uniqueMakes.length > 0 && (
          <div className="filter-section">
            <div className="filter-container">
              <label htmlFor="make-filter" className="filter-label">
                Filter Brand:
              </label>
              <select 
                id="make-filter"
                className="filter-select"
                value={selectedMake}
                onChange={(e) => setSelectedMake(e.target.value)}
              >
                <option value="all">All Brands ({cars.length})</option>
                {uniqueMakes.map(make => {
                  const count = cars.filter(car => car.make === make).length;
                  return (
                    <option key={make} value={make}>
                      {make} ({count})
                    </option>
                  );
                })}
              </select>
            </div>
            
            <div className="filter-container">
              <label htmlFor="sort-filter" className="filter-label">
                Sort by:
              </label>
              <select 
                id="sort-filter"
                className="filter-select"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
              >
                <option value="default">Popularity</option>
                <option value="low-to-high">Price Low to High</option>
                <option value="high-to-low">Price High to Low</option>
              </select>
            </div>

            {(selectedMake !== 'all' || sortOrder !== 'default') && (
              <button 
                className="clear-filter-btn"
                onClick={() => {
                  setSelectedMake('all');
                  setSortOrder('default');
                }}
              >
                Clear All
              </button>
            )}
          </div>
        )}

        <div className="cars-container">
          {sortedAndFilteredCars.length === 0 ? (
            <p className="no-cars-message">
              {selectedMake === 'all' 
                ? 'No cars available' 
                : `No cars available from ${selectedMake}`}
            </p>
          ) : (
            <div className="cars-grid">
              {sortedAndFilteredCars.map((car, index) => (
                <CarCard 
                  key={car.link || car.id || `${car.make}-${car.model}-${car.image}-${index}`}
                  car={car}
                  category={category}
                />
              ))}
            </div>
          )}
        </div>
      </Header>
      
      <Footer />
    </div>
  );
}

export default Listing;
